import { ReactNode } from "react";

interface ButtonProps {
  children: ReactNode;
  variant?: "primary" | "secondary" | "error" | "ghost-error";
  size?: "sm" | "md" | "lg" | "xl";
  type?: "button" | "submit" | "reset";
  onClick?: () => void;
  disabled?: boolean;
  loading?: boolean;
  loadingText?: string;
  className?: string;
}

export default function Button({
  children,
  variant = "primary",
  size = "md",
  type = "button",
  onClick,
  disabled = false,
  loading = false,
  loadingText,
  className = "",
}: ButtonProps) {
  const baseClasses =
    "inline-flex items-center font-bold rounded hover:cursor-pointer focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed";

  const variantClasses = {
    primary:
      "bg-primary text-black border border-primary hover:bg-primary-hover focus:ring-none focus:ring-opacity-50 font-medium",
    secondary:
      "text-black-600 border border-gray-300 hover:border-black focus:ring-none focus:ring-opacity-50 font-medium",
    error:
      "bg-red-600 text-white border border-red-600 hover:bg-red-700 focus:ring-none focus:ring-opacity-50 font-medium",
    "ghost-error":
      "bg-transparent text-red-600 border-none hover:text-red-700 focus:ring-none font-medium",
  };

  const sizeClasses = {
    sm: "px-2 py-1 text-xs",
    md: "px-4 py-2 text-sm",
    lg: "px-6 py-2 text-sm",
    xl: "px-10 py-3 text-base",
  };

  const classes = `${baseClasses} ${variantClasses[variant]} ${sizeClasses[size]} ${className}`;
  const isDisabled = disabled || loading;

  return (
    <button
      type={type}
      className={classes}
      onClick={onClick}
      disabled={isDisabled}
    >
      {loading ? (
        <div className="flex items-center">
          <svg
            className="animate-spin -ml-1 mr-3 h-4 w-4 text-current"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            ></circle>
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            ></path>
          </svg>
          {loadingText || children}
        </div>
      ) : (
        children
      )}
    </button>
  );
}
